// Section wrapper for the SOW register inside the combined Variations & SOWs
// tab. Per brief §5 layer 2 + brand SKILL.md §9.6, the section header lives
// inside the section (title + subtitle + primary `+ Add SOW` button) — the
// shared summary strip lives above, on the tab itself.
//
// The filter slot accepts a complete chip bar (search + multi-select chips
// + binary include-archived chip). Callers that don't yet supply a chip bar
// fall back to the include-archived toggle. The footer summary is a single
// count line so the user can verify the table shows everything that survived
// the filters.

import { Plus } from "lucide-react";
import Button from "../../../common/Button";
import type { ReactNode } from "react";

interface SowSectionProps {
  totalCount: number;
  visibleCount: number;
  includeArchived: boolean;
  onToggleArchived: (next: boolean) => void;
  onAdd: () => void;
  /** Filter affordance row. When supplied, replaces the default
   *  include-archived toggle entirely. */
  filterBar?: ReactNode;
  /** Foot-of-section content rendered below the table. When supplied,
   *  replaces the auto "Showing X of Y SOWs" count line — caller is
   *  expected to pass a TrackingPagination control that already
   *  carries its own count caption. */
  footer?: ReactNode;
  /** The section body (table + inline expands). */
  children: ReactNode;
}

export default function SowSection({
  totalCount,
  visibleCount,
  includeArchived,
  onToggleArchived,
  onAdd,
  filterBar,
  footer,
  children,
}: SowSectionProps) {
  return (
    <section
      aria-labelledby="sow-section-heading"
      className="space-y-3"
      data-testid="sow-section"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            id="sow-section-heading"
            className="text-h3 text-slate-900 dark:text-slate-50"
          >
            Statements of Work
          </h2>
          <p className="text-body-sm text-slate-500 dark:text-slate-400 mt-1">
            Scoped engagements commissioned under this contract. Each SOW
            tracks its own period, value, and lifecycle.
          </p>
        </div>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={onAdd}
          data-testid="sow-section-add"
        >
          Add SOW
        </Button>
      </div>

      {filterBar ? (
        filterBar
      ) : (
        <div className="flex items-center">
          <label className="inline-flex items-center gap-2 text-body-sm text-slate-700 dark:text-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => onToggleArchived(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-800"
              data-testid="sow-section-include-archived"
            />
            Include archived
          </label>
        </div>
      )}

      {children}

      {footer ?? (
        <div
          className="flex justify-end text-caption text-slate-500 dark:text-slate-400"
          data-testid="sow-section-footer"
        >
          Showing {visibleCount} of {totalCount} SOW
          {totalCount === 1 ? "" : "s"}
        </div>
      )}
    </section>
  );
}
